import React, { useEffect, useState } from "react";
import "./admin-edit-user.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getUser, toggleBanUserForAdmin, updateUserForAdmin } from "../../api/user.api";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import { HashLoader } from "react-spinners";
import ModifyUserBalanceModal from "../../components/modal/modify-user-balance";
import ConfirmModal from "../../components/modal/confirm-modal";
import { TbRefresh } from "react-icons/tb";
import { getUserLogin } from "../../api/interaction.api";
import { getUserDepositHistory, getUserActionHistory } from "../../api/transaction.api";
import { getUserRecentOrders } from "../../api/order.api";
import { paymentMethodText, productTypeText, statusText } from "../../utils";
import { useUser } from "../../context/UserContext";

const PREVIEW_LIMIT = 10;

export const AdminEditUser = () => {
  const { user: currentUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBanConfirmOpen, setIsBanConfirmOpen] = useState(false);
  const [historyPreview, setHistoryPreview] = useState({
    login: [],
    deposits: [],
    actions: [],
    orders: [],
  });
  const [historyTotals, setHistoryTotals] = useState({
    login: 0,
    deposits: 0,
    actions: 0,
    orders: 0,
  });
  const { userId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
    fetchHistoryPreview();
  }, [userId]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await getUser(userId);
      if (res?.success) {
        setUserData(res.data.user || null);
        document.title = `Chỉnh sửa người dùng: ${res.data.user?.username || userId}`;
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryPreview = async () => {
    try {
      const [loginRes, depositRes, actionRes, orderRes] = await Promise.all([
        getUserLogin(userId, PREVIEW_LIMIT, 1),
        getUserDepositHistory(userId, PREVIEW_LIMIT, 1),
        getUserActionHistory(userId, PREVIEW_LIMIT, 1),
        getUserRecentOrders(userId, PREVIEW_LIMIT, 1),
      ]);

      setHistoryPreview({
        login: loginRes?.data?.interactions || [],
        deposits: depositRes?.data?.deposit_history || [],
        actions: actionRes?.data?.actions || [],
        orders: orderRes?.data?.orders || [],
      });

      setHistoryTotals({
        login: Number(loginRes?.data?.total || 0),
        deposits: Number(depositRes?.data?.total || 0),
        actions: Number(actionRes?.data?.total || 0),
        orders: Number(orderRes?.data?.total || 0),
      });
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const isCurrentAdmin = currentUser?.role === "admin";
  const canBanTarget = isCurrentAdmin || userData?.role !== "admin";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const formErrors = {};
    if (!userData?.email) {
      formErrors.email = "Email là bắt buộc";
    } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
      formErrors.email = "Email không hợp lệ";
    }

    if (userData?.newPassword && userData.newPassword.length < 6) {
      formErrors.newPassword = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    if (userData?.newPassword && userData.newPassword !== userData?.confirmPassword) {
      formErrors.confirmPassword = "Mật khẩu không khớp";
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setLoading(false);
      return;
    }

    try {
      const res = await updateUserForAdmin(userId, userData);
      if (res?.success) {
        setUserData(res?.data?.updatedUser || null);
        showToast("Cập nhật thành công", "success");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBanToggle = async () => {
    if (!userData?._id) return;
    try {
      setLoading(true);
      const res = await toggleBanUserForAdmin(userData._id, !userData?.isBanned);
      if (res?.success) {
        showToast(`${userData.isBanned ? "Gỡ chặn" : "Chặn"} người dùng thành công`, "success");
        setUserData(res.data.updatedUser || null);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <section className="aue-page">
      <ToastNotification />
      <ModifyUserBalanceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Chỉnh sửa số dư"
        userId={userId}
        message="Bạn đang chỉnh sửa số dư cho người dùng"
        onSuccess={async () => {
          await Promise.all([fetchUser(), fetchHistoryPreview()]);
        }}
      />
      <ConfirmModal
        isOpen={isBanConfirmOpen}
        onClose={() => setIsBanConfirmOpen(false)}
        onConfirm={async () => {
          setIsBanConfirmOpen(false);
          await handleBanToggle();
        }}
        title={userData?.isBanned ? "Gỡ chặn người dùng" : "Chặn người dùng"}
        message={
          userData?.isBanned
            ? "Bạn có chắc muốn gỡ chặn tài khoản này?"
            : "Bạn có chắc muốn chặn tài khoản này? Người dùng sẽ không thể đăng nhập."
        }
      />

      <header className="aue-header">
        <div>
          <p className="aue-breadcrumb">
            <Link to="/super-admin/users">Quản trị / Người dùng</Link> / Chỉnh sửa
          </p>
          <h1>
            Chỉnh sửa tài khoản <span>#{userId}</span>
          </h1>
        </div>

        <div className="aue-header-actions">
          <button type="button" className="aue-btn aue-btn-ghost" onClick={fetchUser}>
            <TbRefresh />
            Làm mới
          </button>
          <button type="button" className="aue-btn aue-btn-primary" onClick={() => navigate(`/super-admin/users/view/${userId}`)}>
            Quay lại hồ sơ
          </button>
        </div>
      </header>

      <form className="aue-layout" onSubmit={handleSubmit}>
        <aside className="aue-sidebar">
          <div className="aue-card aue-profile-card">
            <img src={userData?.profileImg} alt="avatar" className="aue-avatar" />
            <h2>{userData?.username || "-"}</h2>
            <p>{userData?.email || "-"}</p>

            <button
              type="button"
              className="aue-btn aue-btn-primary aue-full-btn"
              onClick={() => setIsModalOpen(true)}
              disabled={!isCurrentAdmin}
            >
              Chỉnh sửa số dư
            </button>
            {canBanTarget && (
              <button
                type="button"
                className={`aue-btn aue-full-btn ${userData?.isBanned ? "aue-btn-success" : "aue-btn-danger"}`}
                onClick={() => setIsBanConfirmOpen(true)}
              >
                {userData?.isBanned ? "Gỡ chặn người dùng" : "Chặn người dùng"}
              </button>
            )}
          </div>
        </aside>

        <main className="aue-main">
          <section className="aue-card">
            <h3>Thông tin cá nhân</h3>
            <div className="aue-form-grid">
              <FormField
                label="Họ và tên"
                name="fullName"
                value={userData?.fullName || ""}
                onChange={handleChange}
                error={errors.fullName}
              />

              <FormField
                label="Tên người dùng"
                name="username"
                value={userData?.username || ""}
                onChange={handleChange}
                disabled
                note="Tên người dùng không thể thay đổi"
              />

              <FormField
                label="Email"
                type="email"
                name="email"
                value={userData?.email || ""}
                onChange={handleChange}
                error={errors.email}
              />

              <FormField
                label="Số điện thoại"
                type="tel"
                name="phone"
                value={userData?.phone || "Chưa cập nhật"}
                onChange={handleChange}
                disabled
                note="Số điện thoại không thể thay đổi"
              />
            </div>
          </section>

          <section className="aue-card">
            <h3>Mật khẩu</h3>
            <p className="aue-note">Để trống nếu bạn không muốn thay đổi mật khẩu.</p>
            <div className="aue-form-grid">
              <FormField
                label="Mật khẩu mới"
                type="password"
                name="newPassword"
                value={userData?.newPassword || ""}
                onChange={handleChange}
                error={errors.newPassword}
              />

              <FormField
                label="Xác nhận mật khẩu mới"
                type="password"
                name="confirmPassword"
                value={userData?.confirmPassword || ""}
                onChange={handleChange}
                error={errors.confirmPassword}
              />
            </div>
          </section>

          <div className="aue-submit-row">
            <button
              type="button"
              className="aue-btn aue-btn-ghost"
              onClick={() => navigate(`/super-admin/users/view/${userId}`)}
            >
              Hủy
            </button>
            <button type="submit" className="aue-btn aue-btn-primary">
              Lưu thay đổi
            </button>
          </div>

          <section className="aue-card">
            <div className="aue-history-head">
              <h3>Lịch sử đăng nhập</h3>
              {historyTotals.login > PREVIEW_LIMIT && (
                <button
                  type="button"
                  className="aue-link-btn"
                  onClick={() => navigate(`/super-admin/users/edit/${userId}/history/login`)}
                >
                  Xem tất cả
                </button>
              )}
            </div>
            <SimpleHistoryTable
              headers={["#", "Hành động", "Thời gian"]}
              rows={(historyPreview.login || []).map((row, index) => [
                index + 1,
                "Đăng nhập",
                row?.createdAt ? new Date(row.createdAt).toLocaleString("vi-VN") : "-",
              ])}
              emptyText="Không có dữ liệu đăng nhập"
            />
          </section>

          <section className="aue-card">
            <div className="aue-history-head">
              <h3>Lịch sử nạp tiền</h3>
              {historyTotals.deposits > PREVIEW_LIMIT && (
                <button
                  type="button"
                  className="aue-link-btn"
                  onClick={() => navigate(`/super-admin/users/edit/${userId}/history/deposits`)}
                >
                  Xem tất cả
                </button>
              )}
            </div>
            <SimpleHistoryTable
              headers={["#", "Mã GD", "Số tiền", "Phương thức", "Trạng thái"]}
              rows={(historyPreview.deposits || []).map((row, index) => [
                index + 1,
                row?.transactionId || "-",
                `${Number(row?.amount || 0).toLocaleString("vi-VN")}đ`,
                paymentMethodText(row?.paymentMethod) || "NA",
                statusText(row?.transactionStatus) || "-",
              ])}
              emptyText="Không có dữ liệu nạp tiền"
            />
          </section>

          <section className="aue-card">
            <div className="aue-history-head">
              <h3>Lịch sử chỉnh sửa số dư</h3>
              {historyTotals.actions > PREVIEW_LIMIT && (
                <button
                  type="button"
                  className="aue-link-btn"
                  onClick={() => navigate(`/super-admin/users/edit/${userId}/history/actions`)}
                >
                  Xem tất cả
                </button>
              )}
            </div>
            <SimpleHistoryTable
              headers={["#", "Số tiền", "Người", "Lý do", "Thời gian"]}
              rows={(historyPreview.actions || []).map((row, index) => [
                index + 1,
                `${row?.transactionType === "add" ? "+" : "-"} ${Number(row?.amount || 0).toLocaleString("vi-VN")}đ`,
                row?.author || "-",
                row?.reason || "-",
                row?.createdAt ? new Date(row.createdAt).toLocaleString("vi-VN") : "-",
              ])}
              emptyText="Không có dữ liệu chỉnh sửa"
            />
          </section>

          <section className="aue-card">
            <div className="aue-history-head">
              <h3>Lịch sử mua hàng</h3>
              {historyTotals.orders > PREVIEW_LIMIT && (
                <button
                  type="button"
                  className="aue-link-btn"
                  onClick={() => navigate(`/super-admin/users/edit/${userId}/history/orders`)}
                >
                  Xem tất cả
                </button>
              )}
            </div>
            <SimpleHistoryTable
              headers={["#", "Mã đơn", "Số tiền", "Loại", "Trạng thái"]}
              rows={(historyPreview.orders || []).map((row, index) => [
                index + 1,
                row?.orderId || "-",
                `${Number(row?.order_final_amount || 0).toLocaleString("vi-VN")}đ`,
                productTypeText(row?.order_type),
                statusText(row?.order_status) || "-",
              ])}
              emptyText="Không có dữ liệu đơn hàng"
            />
          </section>
        </main>
      </form>
    </section>
  );
};

const FormField = ({ label, note, error, disabled, ...props }) => {
  return (
    <label className="aue-field">
      <span>{label}</span>
      <input className={`aue-input ${error ? "aue-input-error" : ""}`} disabled={disabled} {...props} />
      {note ? <small>{note}</small> : null}
      {error ? <small className="aue-error">{error}</small> : null}
    </label>
  );
};

const SimpleHistoryTable = ({ headers, rows, emptyText }) => {
  return (
    <div className="aue-history-table-wrap">
      <table className="aue-history-table">
        <thead>
          <tr>
            {headers.map((head) => (
              <th key={head}>{head}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, idx) => (
              <tr key={`${idx}-${row[1] || "item"}`}>
                {row.map((cell, i) => (
                  <td key={`${idx}-${i}`}>{cell}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length} className="aue-history-empty">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
